import { useMemo } from "react";
import {
  Circle,
  CheckCircle2,
  Loader,
  XCircle,
  FileText,
  FileSpreadsheet,
  FileImage,
  Presentation,
  FileType2,
  Tag,
  FolderOpen,
} from "lucide-react";
import type { FilterCategoryGroup } from "@/components/ui";
import type { Tag as TagType } from "@/components/ui/Tag/types";
import type { DocumentCategoryView } from "@/packages/types";
import { useDocumentCategoryLabel } from "./use-document-category-label";
import { useTranslation } from "@/packages/utils";
import type {
  FilterCategoryGroupKey,
  FilterCategoryKey,
  FilterValueKey,
} from "@/components/audits/tabs/checklist/checklist-filter-data";

/**
 * Document-specific filter categories for the Filter component.
 *
 * Status values map to `summaryStatus` backend field via mapStatus() in types.ts:
 *   - "draft"        → summaryStatus is null/undefined (no summary)
 *   - "final"        → summaryStatus === "completed"
 *   - "under-review" → summaryStatus === "generating" | "retrying"
 *   - "expired"      → summaryStatus === "failed"
 *
 * File Type values map to the `fileType` backend field directly.
 * Tag values are dynamically populated from the database.
 */

const BASE_FILTER_CATEGORIES: FilterCategoryGroupKey[] = [
  {
    labelKey: "filters.properties",
    categories: [
      {
        id: "status",
        labelKey: "filters.status",
        icon: Circle,
        allowedOperators: ["is_either_of", "is_not"],
        values: [
          {
            id: "draft",
            labelKey: "filters.draft",
            icon: Circle,
            color: "#6B6B6B",
          },
          {
            id: "under-review",
            labelKey: "filters.underReview",
            icon: Loader,
            color: "#E8862A",
          },
          {
            id: "final",
            labelKey: "filters.final",
            icon: CheckCircle2,
            color: "#4CAF50",
          },
          {
            id: "expired",
            labelKey: "filters.expired",
            icon: XCircle,
            color: "#9B9B9B",
          },
        ],
      },
      {
        id: "file_type",
        labelKey: "filters.fileType",
        icon: FileText,
        allowedOperators: ["is", "is_not", "is_either_of"],
        values: [
          {
            id: "pdf",
            labelKey: "filters.pdf",
            icon: FileText,
            color: "#E53935",
          },
          {
            id: "docx",
            labelKey: "filters.docx",
            icon: FileType2,
            color: "#2196F3",
          },
          {
            id: "xlsx",
            labelKey: "filters.xlsx",
            icon: FileSpreadsheet,
            color: "#4CAF50",
          },
          {
            id: "pptx",
            labelKey: "filters.pptx",
            icon: Presentation,
            color: "#FF9800",
          },
          {
            id: "txt",
            labelKey: "filters.txt",
            icon: FileType2,
            color: "#9E9E9E",
          },
          {
            id: "image",
            labelKey: "filters.image",
            icon: FileImage,
            color: "#AB47BC",
          },
        ],
      },
      {
        id: "tags",
        labelKey: "filters.tags",
        icon: Tag,
        allowedOperators: ["includes_any", "includes_all", "includes_none"],
        values: [],
      },
      {
        id: "category",
        labelKey: "filters.category",
        icon: FolderOpen,
        allowedOperators: ["is", "is_not", "is_either_of"],
        values: [],
      },
    ],
  },
];

function resolveFilterCategories(
  groups: FilterCategoryGroupKey[],
  t: (key: string) => string,
  tags: TagType[],
  categories: DocumentCategoryView[],
  getCategoryLabel: (category: {
    name: string;
    slug?: string | null;
  }) => string,
): FilterCategoryGroup[] {
  return groups.map((group) => ({
    label: t(group.labelKey),
    categories: group.categories.map((cat) => ({
      id: cat.id,
      label: t(cat.labelKey),
      icon: cat.icon,
      allowedOperators: cat.allowedOperators,
      values: resolveCategoryValues(cat, t, tags, categories, getCategoryLabel),
    })),
  }));
}

function resolveCategoryValues(
  category: FilterCategoryKey,
  t: (key: string) => string,
  tags: TagType[],
  documentCategories: DocumentCategoryView[],
  getCategoryLabel: (category: {
    name: string;
    slug?: string | null;
  }) => string,
): FilterCategoryGroup["categories"][number]["values"] {
  if (category.id === "tags") {
    return tags.map((tag) => ({
      id: tag.id,
      label: tag.name,
      color: tag.color,
    }));
  }
  if (category.id === "category") {
    return documentCategories.map((cat) => ({
      id: cat._id,
      label: getCategoryLabel({ name: cat.name, slug: cat.slug }),
    }));
  }
  return category.values.map((val: FilterValueKey) => ({
    id: val.id,
    label: t(val.labelKey),
    ...(val.icon && { icon: val.icon }),
    ...(val.color && { color: val.color }),
  }));
}

export function useDocumentFilterCategories(
  tags: TagType[],
  categories: DocumentCategoryView[],
): FilterCategoryGroup[] {
  const { t } = useTranslation("documents");
  const getCategoryLabel = useDocumentCategoryLabel();
  return useMemo(
    () =>
      resolveFilterCategories(
        BASE_FILTER_CATEGORIES,
        (key) => t(key as "filters.properties") as string,
        tags,
        categories,
        getCategoryLabel,
      ),
    [t, tags, categories, getCategoryLabel],
  );
}

/** @deprecated Use useDocumentFilterCategories instead */
export function getDocumentFilterCategories(
  tags: TagType[],
  categories: DocumentCategoryView[],
): FilterCategoryGroup[] {
  const labels: Record<string, string> = {
    "filters.properties": "Document Properties",
    "filters.status": "Status",
    "filters.draft": "Draft",
    "filters.underReview": "Under Review",
    "filters.final": "Final",
    "filters.expired": "Expired",
    "filters.fileType": "File Type",
    "filters.pdf": "PDF",
    "filters.docx": "DOCX",
    "filters.xlsx": "XLSX",
    "filters.pptx": "PPTX",
    "filters.txt": "TXT",
    "filters.image": "Image",
    "filters.tags": "Tags",
    "filters.category": "Category",
  };
  return resolveFilterCategories(
    BASE_FILTER_CATEGORIES,
    (key) => labels[key] ?? key,
    tags,
    categories,
    (category) => category.name,
  );
}
